import { sendEmailVerification } from "firebase/auth";
import React, { useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import FullPageLayout from "../../components/FullPageLayout/FullPageLayout";
import { auth } from "../../firebase";
import { useAppAuthState } from "../../hooks/use-app-auth-state";
import { SitePage } from "../../types/SitePage";

import { usePollForVerifiedEmail } from "../../hooks/use-poll-for-verified-email";
import "./VerifyEmailPage.scss";

const VerifyEmailPage: React.FC = () => {
  const navigate = useNavigate();
  usePollForVerifiedEmail(() => {
    navigate("/home");
  });

  const { unverifiedFireUser, isAuthLoading } = useAppAuthState();
  const location = useLocation();
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  const handleResendVerification = async () => {
    setError(null);
    setMessage(null);

    if (unverifiedFireUser) {
      setIsResending(true);

      try {
        await sendEmailVerification(unverifiedFireUser);
        setMessage(
          "Verification email sent. Please check your inbox (and spam folder).",
        );
      } catch (err: any) {
        setError(
          "Failed to resend verification email. Please try again later.",
        );
      } finally {
        setIsResending(false);
      }
    } else {
      setError("You are not logged in.");
    }
  };

  if (isAuthLoading) {
    return (
      <FullPageLayout page={SitePage.VerifyEmail} title="Verify Email">
        <div className="verify-email-page-container">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </FullPageLayout>
    );
  }

  if (!unverifiedFireUser && !isAuthLoading) {
    // If not logged in and auth state is resolved, redirect to login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (unverifiedFireUser?.emailVerified) {
    // If email is verified, redirect to app home
    return <Navigate to="/home" replace />;
  }

  return (
    <FullPageLayout page={SitePage.VerifyEmail} title="Verify Your Email">
      <h2>Verify Your Email</h2>
      <div className="verify-email-page-container">
        <p>
          We've sent a verification link to{" "}
          <strong>{unverifiedFireUser?.email}</strong>. Please click the link in
          the email to activate your account.
        </p>
        {message && <Alert variant="success">{message}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        <div className="page-actions">
          <Button
            variant="primary"
            onClick={handleResendVerification}
            disabled={isResending}
          >
            {isResending ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                />{" "}
                Sending...
              </>
            ) : (
              "Resend Verification Email"
            )}
          </Button>
        </div>
        <div className="info">
          If you don't see the email, please check your spam folder.
        </div>
        <hr />
        <Button variant="primary" onClick={() => auth.signOut()}>
          Logout
        </Button>
      </div>
    </FullPageLayout>
  );
};

export default VerifyEmailPage;
